import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import { FinalQuizResponse, FinalQuizResultResponse, LearningQuestion } from '../../core/models/portal.models';

@Component({
  selector: 'app-final-knowledge-check-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './final-knowledge-check-page.component.html'
})
export class FinalKnowledgeCheckPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly quiz = signal<FinalQuizResponse | null>(null);
  protected readonly result = signal<FinalQuizResultResponse | null>(null);
  protected readonly selectedOptions = signal<Record<number, number>>({});
  protected readonly currentQuestionIndex = signal(0);
  protected readonly error = signal<string | null>(null);
  protected readonly saving = signal(false);
  private enrollmentId = 0;

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.enrollmentId = Number(params.get('enrollmentId'));
      this.quiz.set(null);
      this.result.set(null);
      this.selectedOptions.set({});
      this.currentQuestionIndex.set(0);
      this.load();
    });
  }

  protected selectOption(questionId: number, optionId: number): void {
    this.selectedOptions.update((current) => ({ ...current, [questionId]: optionId }));
  }

  protected selectedOptionId(questionId: number): number | null {
    return this.selectedOptions()[questionId] ?? null;
  }

  protected currentQuestion(questions: LearningQuestion[]): LearningQuestion | null {
    return questions[this.currentQuestionIndex()] ?? null;
  }

  protected isLastQuestion(questions: LearningQuestion[]): boolean {
    return this.currentQuestionIndex() === questions.length - 1;
  }

  protected answeredCount(questions: LearningQuestion[]): number {
    return questions.filter((question) => Boolean(this.selectedOptionId(question.id))).length;
  }

  protected goToPreviousQuestion(): void {
    this.error.set(null);
    this.currentQuestionIndex.update((index) => Math.max(0, index - 1));
  }

  protected goToNextQuestion(item: FinalQuizResponse): void {
    const question = this.currentQuestion(item.questions);
    if (!question || !this.selectedOptionId(question.id)) {
      this.error.set('Bitte beantworte diese Frage, bevor du fortfährst.');
      return;
    }

    this.error.set(null);
    this.currentQuestionIndex.update((index) => Math.min(item.questions.length - 1, index + 1));
  }

  protected submitQuiz(item: FinalQuizResponse): void {
    if (!item.questions.every((question) => Boolean(this.selectedOptionId(question.id)))) {
      this.error.set('Bitte beantworte alle Fragen.');
      return;
    }

    this.saving.set(true);
    this.error.set(null);
    this.api.submitFinalQuiz(this.enrollmentId, {
      answers: item.questions.map((question) => ({
        questionId: question.id,
        selectedOptionId: this.selectedOptionId(question.id) ?? 0
      }))
    }).subscribe({
      next: (result) => {
        this.result.set(result);
        this.quiz.set(result.quiz);
        this.saving.set(false);

        if (result.passed) {
          this.router.navigate(
            ['/learning', this.enrollmentId, 'final-knowledge-check', 'result'],
            { replaceUrl: true }
          );
        }
      },
      error: () => {
        this.error.set('Der Abschluss-Wissens-Check konnte nicht ausgewertet werden.');
        this.saving.set(false);
      }
    });
  }

  private load(): void {
    this.api.finalQuiz(this.enrollmentId).subscribe({
      next: (quiz) => this.quiz.set(quiz),
      error: () => this.error.set('Der Abschluss-Wissens-Check konnte nicht geladen werden.')
    });
  }
}
